import { CloseIcon, LabelChip, Text } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  LABEL_TERM_KEY_SUGGESTION_LIMIT,
  MANUAL_TERM_SOURCE,
  normalizeTermKey,
  searchLabelTermKeys,
  searchLabelTerms,
  TERM_KEY_MAX_LENGTH,
  TERM_VALUE_MAX_LENGTH,
  type EpisodeTerm,
  type LabelTerm,
  type LabelTermKey,
  type TermKeyResult,
} from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { FilterSearchCombobox } from "@/components/filter";
import styles from "./EpisodeTermEditor.module.css";

// 타이핑이 멈춘 뒤 후보를 조회할 때까지의 지연. 목록 필터의 값과 같다. 같은 성질의 지연을
// 칸마다 다르게 두면 사용자는 어느 것이 느린지 구분할 수 없다.
const SUGGEST_DEBOUNCE_MS = 250;

// 표기 칩 색. 결함(호박)과 구분되는 중립 청회다. 이 축은 판정이 아니라 기록이다.
// 그래서 켜짐/꺼짐이 없고, 색이 상태를 나타내지 않는다.
const TERM_CHIP_COLOR = 0x475569;

// 후보 메뉴의 최소 폭(px). 칩과 설명이 한 줄에 들어가는 폭이다. 필터 행 기본값(560)은 팝오버보다 넓다.
const MENU_MIN_WIDTH_PX = 280;

// 독 요약에서 값 하나가 차지하는 최대 글자 수. 넘으면 말줄임표로 줄인다. 전체 값은 툴팁이 보인다.
const SUMMARY_VALUE_MAX_LENGTH = 24;

type TermKeyError = Extract<TermKeyResult, { ok: false }>["reason"];

const KEY_ERROR_TEXT: Record<TermKeyError, string> = {
  blank: "키를 먼저 입력해 주세요.",
  too_long: `키는 ${TERM_KEY_MAX_LENGTH}자까지 쓸 수 있습니다.`,
  invalid_char: "키에는 문자·숫자·밑줄(_)만 쓸 수 있습니다.",
};

const VALUE_TOO_LONG_TEXT = `값은 ${TERM_VALUE_MAX_LENGTH}자까지 쓸 수 있습니다.`;

// 추가 줄 아래에 표시하는 안내. 어느 칸의 문제인지 알아야 그 칸을 invalid 로 표시한다.
type EditorNotice = { field: "key" | "value"; text: string };

export type EpisodeTermEditorProps = {
  terms: readonly EpisodeTerm[];
  // read-only(viewer·guest) 이거나 저장 중. 칩의 삭제 표적과 입력을 함께 잠근다.
  disabled: boolean;
  // 어휘 한 건을 더한다. 소비처가 `withTermAdded` 로 draft 에 반영한다.
  onAddTerm: (termKey: string, termValue: string) => void;
  // 어휘 한 건을 지운다. 소비처가 `withTermRemoved` 로 draft 에 반영한다.
  onRemoveTerm: (term: EpisodeTerm) => void;
};

/**
 * 표기 편집 본문. 위에는 붙은 어휘를 키별로 묶어 보이고, 아래에는 `[키][값]` 추가 줄을 둔다.
 *
 * 키와 값 모두 열린 입력이다. 그래서 칩 목록이 아니라 **입력 + 자동완성**이다. 자동완성이 이
 * 화면의 핵심이다. 서버 정규화는 대소문자와 공백만 접는다. 그래서 `obj` / `object` 와 오타는
 * 사전에서 별도 행이 되고, 한 번 나뉜 행을 합칠 경로가 없다. 이미 있는 키와 값을 보여 주는 것이
 * 유일한 방어다.
 *
 * 태그 축처럼 **즉시 draft 에 반영한다**(확인 버튼이 없다). 값을 더한 뒤에는 값 칸만 비운다.
 * 같은 키의 값을 이어서 입력하는 경우가 많기 때문이다.
 *
 * Owns: 키별 칩·추가 줄·후보 조회·입력 안내.
 * Does not own: 팝오버 셸·단축키·draft(소비처가 갖는다).
 */
export function EpisodeTermEditor({
  terms,
  disabled,
  onAddTerm,
  onRemoveTerm,
}: EpisodeTermEditorProps): ReactNode {
  const [keyQuery, setKeyQuery] = useState("");
  const [valueQuery, setValueQuery] = useState("");
  const [notice, setNotice] = useState<EditorNotice | null>(null);
  const valueInputRef = useRef<HTMLInputElement | null>(null);
  const keys = useTermKeys();
  const normalizedKey = normalizeTermKey(keyQuery);
  const values = useTermValueSuggestions(normalizedKey.ok ? normalizedKey.key : null, valueQuery);

  const commitKey = (raw: string): void => {
    const result = normalizeTermKey(raw);
    if (!result.ok) {
      setNotice({ field: "key", text: KEY_ERROR_TEXT[result.reason] });
      return;
    }
    // 서버가 저장할 모양을 칸에 바로 보인다. 그러면 사용자가 새 키를 덜 만든다.
    setKeyQuery(result.key);
    setNotice(null);
    valueInputRef.current?.focus();
  };

  const commitValue = (raw: string): void => {
    const value = raw.trim();
    if (value === "") {
      return;
    }
    const result = normalizeTermKey(keyQuery);
    if (!result.ok) {
      setNotice({ field: "key", text: KEY_ERROR_TEXT[result.reason] });
      return;
    }
    if ([...value].length > TERM_VALUE_MAX_LENGTH) {
      setNotice({ field: "value", text: VALUE_TOO_LONG_TEXT });
      return;
    }
    onAddTerm(result.key, value);
    setKeyQuery(result.key);
    setValueQuery("");
    setNotice(null);
  };

  return (
    <div className={styles.editor}>
      {groupTermsByKey(terms).map(([termKey, group]) => (
        <section key={termKey} className={styles.group}>
          <span className={styles.groupKey}>{termKey}</span>
          <div className={styles.chips}>
            {group.map((term) => (
              <TermChip
                key={`${term.source}:${term.term_value}`}
                term={term}
                disabled={disabled}
                onRemove={onRemoveTerm}
              />
            ))}
          </div>
        </section>
      ))}

      {/* 후보 메뉴는 팝오버 밖 레이어에 뜬다. 팝오버는 넘치는 내용을 자르므로, 안에 두면 맨 아래
          추가 줄의 메뉴가 보이지 않는다. */}
      <div className={styles.addRow}>
        <FilterSearchCombobox
          inputSize="sm"
          className={styles.keyInput}
          value={keyQuery}
          candidates={keys.items.map((item) => ({
            value: item.term_key,
            label: item.term_key,
            // 이 키로 등록된 값의 종류 수. 많이 쓰는 키를 알아보게 한다.
            description: `값 ${item.term_count}종`,
            color: TERM_CHIP_COLOR,
          }))}
          sectionLabel="일치하는 키"
          emptySectionLabel="자주 쓰는 키"
          placeholder="키"
          ariaLabel="표기 키"
          minMenuWidth={MENU_MIN_WIDTH_PX}
          showClear={false}
          disabled={disabled}
          invalid={notice?.field === "key"}
          loading={!keys.settled}
          loadingMessage="키 목록을 불러오는 중…"
          emptyMessage="사전에 없는 키입니다. Enter 로 새 키를 씁니다."
          onValueChange={(next) => {
            setKeyQuery(next);
            setNotice(null);
          }}
          // Enter(고른 후보가 없을 때)와 후보 선택 모두 키를 확정하고 값 칸으로 넘어간다.
          onSearch={commitKey}
          onSelectCandidate={(candidate) => commitKey(candidate.value)}
        />
        <FilterSearchCombobox
          inputRef={valueInputRef}
          inputSize="sm"
          className={styles.valueInput}
          value={valueQuery}
          candidates={values.items.map((item) => ({
            value: item.term_value,
            label: item.term_value,
            // 이 표기를 이미 쓴 에피소드 수. 이 신호는 사용자가 새 표기를 만들 필요를 줄인다.
            description: `${item.episode_count}개 에피소드`,
            color: TERM_CHIP_COLOR,
          }))}
          sectionLabel="일치하는 값"
          emptySectionLabel="이 키의 값"
          placeholder="값 입력 후 Enter"
          ariaLabel="표기 값"
          minMenuWidth={MENU_MIN_WIDTH_PX}
          showClear={false}
          disabled={disabled}
          invalid={notice?.field === "value"}
          loading={!values.settled}
          loadingMessage="값 목록을 불러오는 중…"
          emptyMessage={
            normalizedKey.ok
              ? "사전에 없는 값입니다. Enter 로 새로 담습니다."
              : "키를 먼저 입력해 주세요."
          }
          onValueChange={(next) => {
            setValueQuery(next);
            setNotice(null);
          }}
          // Enter(고른 후보가 없을 때)와 후보 선택 모두 같은 추가 경로를 쓴다.
          onSearch={commitValue}
          onSelectCandidate={(candidate) => commitValue(candidate.value)}
        />
      </div>

      {notice === null ? null : (
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.warn} role="alert">
          {notice.text}
        </Text>
      )}
    </div>
  );
}

function TermChip({
  term,
  disabled,
  onRemove,
}: {
  term: EpisodeTerm;
  disabled: boolean;
  onRemove: (term: EpisodeTerm) => void;
}): ReactNode {
  const removable = term.source === MANUAL_TERM_SOURCE;
  const text = `${term.term_key}: ${term.term_value}`;
  return (
    // 다른 경로가 붙인 표기는 지울 수 없다. 그래서 삭제 표적(×)을 그리지 않는다.
    // 화면에서만 삭제하고 서버가 무시하면 "지웠는데 돌아온다"로 보인다.
    <LabelChip
      color={TERM_CHIP_COLOR}
      size="xs"
      variant={removable ? "interactive" : "outline"}
      className={styles.chip}
      icon={removable ? <CloseIcon /> : undefined}
      showIcon={false}
      disabled={disabled}
      aria-label={removable ? `${text} 지우기` : text}
      title={removable ? undefined : "다른 경로가 붙인 표기라 여기서는 지울 수 없습니다."}
      onClick={removable ? () => onRemove(term) : undefined}
    >
      {term.term_value}
      {term.canonical_value === null ? null : (
        <span className={styles.canonical}>→ {term.canonical_value}</span>
      )}
    </LabelChip>
  );
}

// 후보 목록과 첫 응답을 받았는지 여부. 받기 전에는 빈 목록을 "없음"으로 표시하지 않는다.
type Suggestions<T> = { items: readonly T[]; settled: boolean };

/**
 * 사전의 키 목록을 한 번 조회한다.
 *
 * 키는 수십 개라 편집기를 열 때 한 번 받고, 거르기는 콤보박스가 화면에서 한다. 실패하면
 * 조용히 빈 목록으로 degrade 한다. 사전을 불러오지 못해도 표기는 입력할 수 있어야 한다.
 */
function useTermKeys(): Suggestions<LabelTermKey> {
  const [state, setState] = useState<Suggestions<LabelTermKey>>({ items: [], settled: false });

  useEffect(() => {
    let cancelled = false;
    void searchLabelTermKeys({ limit: LABEL_TERM_KEY_SUGGESTION_LIMIT })
      .then((items) => {
        if (!cancelled) setState({ items, settled: true });
      })
      .catch(() => {
        if (!cancelled) setState({ items: [], settled: true });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * 한 키의 값 후보를 지연 조회한다. 키가 규칙 밖이면(`null`) 조회하지 않는다.
 *
 * 빈 검색어에도 조회한다. 키를 고르자마자 그 키의 흔한 값이 보여야 사용자가 "이미 있는 것"을
 * 고를 수 있다. 실패하면 조용히 빈 목록으로 degrade 한다. 검색어를 바꾸는 동안에는 이전 후보를
 * 그대로 보인다. 키가 바뀌면 첫 응답 전까지 불러오는 중으로 표시한다.
 */
function useTermValueSuggestions(termKey: string | null, query: string): Suggestions<LabelTerm> {
  const [state, setState] = useState<Suggestions<LabelTerm> & { termKey: string | null }>({
    items: [],
    settled: true,
    termKey: null,
  });

  useEffect(() => {
    if (termKey === null) {
      setState({ items: [], settled: true, termKey: null });
      return;
    }
    let cancelled = false;
    setState((current) =>
      current.termKey === termKey ? current : { items: [], settled: false, termKey },
    );
    const timer = setTimeout(() => {
      void searchLabelTerms({ term_key: termKey, search: query.trim() })
        .then((items) => {
          if (!cancelled) setState({ items, settled: true, termKey });
        })
        .catch(() => {
          if (!cancelled) setState({ items: [], settled: true, termKey });
        });
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [termKey, query]);

  return state;
}

// 어휘를 키별로 묶는다. 키는 사전순이고, 묶음 안은 draft 순서다.
function groupTermsByKey(terms: readonly EpisodeTerm[]): [string, EpisodeTerm[]][] {
  const groups = new Map<string, EpisodeTerm[]>();
  for (const term of terms) {
    const group = groups.get(term.term_key);
    if (group === undefined) {
      groups.set(term.term_key, [term]);
    } else {
      group.push(term);
    }
  }
  return [...groups.entries()].sort(([left], [right]) =>
    left < right ? -1 : left > right ? 1 : 0,
  );
}

/**
 * 독 트리거에 표시할 요약. 키별로 값을 나열한다. 예) `object: rope, gori · reason: early done`
 *
 * 값이 문장일 수 있다(수집 프롬프트). 그래서 값 하나를 24자에서 줄인다. 줄 전체는 트리거의 CSS
 * 말줄임이 줄인다. 전체 값은 `episodeTermLines` 로 툴팁에 보인다.
 */
export function episodeTermSummary(terms: readonly EpisodeTerm[]): string {
  if (terms.length === 0) {
    return "표기 없음";
  }
  return groupTermsByKey(terms)
    .map(([termKey, group]) => {
      const values = group.map((term) => truncateSummaryValue(term.term_value));
      return `${termKey}: ${values.join(", ")}`;
    })
    .join(" · ");
}

// 툴팁에 한 줄씩 표시할 어휘 전체. 요약과 같은 순서이고 값을 줄이지 않는다. 예) `["object: rope"]`
export function episodeTermLines(terms: readonly EpisodeTerm[]): string[] {
  return groupTermsByKey(terms).flatMap(([termKey, group]) =>
    group.map((term) => `${termKey}: ${term.term_value}`),
  );
}

function truncateSummaryValue(value: string): string {
  const chars = [...value];
  if (chars.length <= SUMMARY_VALUE_MAX_LENGTH) {
    return value;
  }
  // 자른 끝의 공백을 지운다. 그러지 않으면 `and …` 처럼 말줄임표가 떨어져 보인다.
  return `${chars.slice(0, SUMMARY_VALUE_MAX_LENGTH).join("").trimEnd()}…`;
}
